// milestone-birthday — y02 Olov Tabriki (yubiley 50).
//
// Paper-invitation preview. A warm-gold foil border wraps a champagne
// inner card. The focal medallion is a large "50" numeral set inside a
// gold-trimmed roundel — the milestone numeral is the design. Eyebrow
// reads YUBILEY. Names in Cormorant Garamond. Footer carries small-caps
// date + venue. Colours come from LayoutCtx so the catalog tile, editor
// preview, and printed card all match.
import { type LayoutCtx } from "./_shared";

const FOIL =
  "linear-gradient(135deg, #8C6A1F 0%, #B8932F 22%, #E5C26B 45%, #B8932F 65%, #8C6A1F 100%)";

export function MilestoneBirthdayLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 14, height: 1, background: accent }} />
          YUBILEY
          <span style={{ width: 14, height: 1, background: accent }} />
        </div>

        {/* "50" numeral medallion */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "6%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {/* Inner gold ring */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: "8%",
                  borderRadius: "50%",
                  border: `1px solid ${accent}`,
                  opacity: 0.5,
                }}
              />
              {/* "50" */}
              <span
                style={{
                  fontFamily: font,
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontSize: "min(56%, 130px)",
                  color: accent,
                  letterSpacing: "-0.03em",
                  fontFeatureSettings: '"lnum" 1, "tnum" 1',
                  lineHeight: 0.86,
                  position: "relative",
                }}
              >
                50
              </span>
              {/* Top + bottom rosette dingbats */}
              <svg
                viewBox="0 0 100 100"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                aria-hidden
              >
                {[12, 88].map((y, i) => (
                  <g key={i} transform={`translate(50 ${y})`}>
                    <circle r="2" fill={accent} />
                    <circle r="3.8" fill="none" stroke={accent} strokeWidth="0.5" opacity="0.7" />
                    <line x1="-9" y1="0" x2="-5" y2="0" stroke={accent} strokeWidth="0.6" opacity="0.8" />
                    <line x1="9" y1="0" x2="5" y2="0" stroke={accent} strokeWidth="0.6" opacity="0.8" />
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.02em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          YUBILEY · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
